import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit, AfterContentInit, HostBinding, OnDestroy } from '@angular/core';
import { SemControlComponent } from '../sem-control/sem-control.component';
import { SemDialogComponent } from '../dialogs/sem-dialog/sem-dialog.component';
import { SemTreeviewDialogComponent } from '../dialogs/sem-treeview-dialog/sem-treeview-dialog.component';
import { keyframes } from '@angular/animations';
import { ValueAccessorBase } from '../shared/value-accessor/value-accessor-base';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SemOneToManyContainerComponent } from '../sem-one-to-many-container/sem-one-to-many-container.component';
import { InlineHit, applyAddOnly, applyAddKeepingField, registerInlineAddRows, narrowHits, bindingsToHide, bindingOfElementName, buildInlineSearchQuery, buildRelatedEntityUrl, defaultDisplayFields, findLinkedRowIndex, linkedEntityIds, matchesWordStart, toHit, inlineDisplayText, inlineRowMode, inlineHideRowMenu, inlineEmptyRowClear, inlineRedirectAddNew, inlineRemoveRow, inlineClearRemovesRow, inlineWidenColumn } from './m2o-inline-search';
import { OneToManySectionComponent } from '../sem-one-to-many-container/one-to-many-section/one-to-many-section.component';


@Component({
    selector: 'sem-many-to-one',
    templateUrl: './sem-many-to-one-control.component.html',
    styleUrls: ['./sem-many-to-one-control.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: SemManyToOneControlComponent, multi: true }
    ],
    standalone: false
})

export class SemManyToOneControlComponent extends ValueAccessorBase<any> implements OnInit, OnDestroy {

  @Input() entityName: any;
  @Input() modelName: any;
  @Input() dialogTitle: any;
  @Input() entityFormUrl: any;
  @Input() searchFields: string[];
  @Input() displayFields: string[];
  /** Backend setting ShowLinkedDataControls. Off by default: the link / linked-data / remove-link buttons are hidden. */
  @Input() showControls: boolean = false;
  @Input() entityReadOnly: boolean = true;
  @Input() readOnly: boolean = true;
  @Input() settingsFormName;
  @Input() additionalSearchFilter;
  @Input() dialogType: string;
  @Input() tooltipTextPoVseh: string;
  @Input() repositories: string = null;
  @Input() clearFiles: string = null;
  @Input() customEvalCode: string = "";
  @Input() onValueSelectValidation: string = "";
  @Input() customApiUrl: string = "";
  @Input() parentBindingProperty: string = "";
  /** Backend setting EnableMultiSelect: the dialog shows checkboxes and returns an array of selected entities. */
  @Input() enableMultiSelect: boolean = false;
  /**
   * Backend setting MultiSelectTargetBinding: property of the root model holding the one-to-many rows that
   * receive one row per selected entity ({ [entityName]: entity }). Falls back to parentBindingProperty
   * (the one-to-many container this control is nested in) when empty.
   */
  @Input() multiSelectTargetBinding: string = "";
  @Output() selectedItemsChange: EventEmitter<any[]> = new EventEmitter<any[]>();
  /**
   * Backend setting InlineSearch: the link button + dialog header is replaced by a search-as-you-type
   * input over the same search API, SearchFields, AdditionalFilterQuery and Repositories as the dialog.
   * Ignored for DialogType "tree" and for an empty EntityName. Off (false) = unchanged behaviour.
   * TODO(A2): multi-select "add to list" mode, auto-hide of the linked field repeating the title,
   * collapsed section until a value is picked, BRD binding-prefix lookup.
   */
  @Input() inlineSearch: boolean = false;

  inlineText: string = "";
  inlineHits: InlineHit[] = [];
  inlineOpen: boolean = false;
  inlineEditing: boolean = false;
  inlineActive: number = 0;
  inlineLoading: boolean = false;
  inlineError: string = null;
  private inlineTimer: any = null;
  private inlineRequest: any = null;
  private inlineSeq: number = 0;
  private static readonly INLINE_MAX_HITS = 8;
  private static readonly INLINE_PAGE_SIZE = 40;
  private static readonly INLINE_FALLBACK_PAGE_SIZE = 200;

  linkWithExistingData: string;
  multiSelectItems: any[] = [];

  /**
   * The detail-page redesign hides <sem-many-to-one> by default (tailwind.css). The host gets this class
   * when the backend setting ShowLinkedDataControls ([showControls]) is on and the control is editable,
   * and the CSS shows the link / linked-data / remove-link buttons only then.
   */
  @HostBinding('class.m2o-inline-search')
  get inlineSearchActive(): boolean {
    return this.inlineSearch === true
      && this.dialogType !== "tree"
      && typeof this.entityName == "string" && this.entityName.length > 0;
  }

  @HostBinding('class.m2o-show-controls')
  get controlsVisible(): boolean {
    return this.showControls && !this.readOnly && this.showLinkDataControls(this.entityName);
  }

  /** InlineSearch: hover titles of the ↗ / × icons beside a picked value (they replace the old Povezan podatek / Odstrani povezavo buttons). */
  get inlineOpenTitle(): string {
    const t = this.translationsService.translations ? this.translationsService.translations["WebGalis_OpenLinkedRecord"] : null;
    return t || "Odpri povezan zapis";
  }

  get inlineRemoveTitle(): string {
    const t = this.translationsService.translations ? this.translationsService.translations["WebGalis_RemoveLink"] : null;
    return t || "Odstrani povezavo";
  }

  dataIsLinked: boolean = false;
  jsTranslations: any;
  _parent: any;

  super() {



  }

  ngOnInit() {

    this.linkWithExistingData = this.translationsService.translations["WebGalis_LinkWithExistingData"];
    this.jsTranslations = this.translationsService.translations;

    if (typeof this.entityName != "undefined" && this.entityName) {
      this.dfms.manyToOne.push({ name: this.entityName, required: this.isRequired });

      if (this.readOnly) {
        if (this.readOnly == true) {
          this.dfms.manyToOneReadOnly.push(this.entityName);
        }
      }

      try {
        if (typeof this.entityName != "undefined" && this.entityName.length > 0) {
          var props = this.entityName.split(".");

          // Nested in a one-to-many row: `this.model` IS the row, so the entity object must be
          // created on the row (row.OtherPerson = {}), never on the root model. Registering the
          // entity in the global dynamicFields list would replay it onto the root, so skip that too.
          const nestedInRow = !!(this.parentBindingProperty && this.parentBindingProperty.length > 0);

          if (!nestedInRow) {
            this.dfms.dynamicFields.push(this.entityName);
          }

          if (props.length == 0) {
            this.model[this.entityName] = {};
          }
          else {
            this.model = this.dfms.assign(this.model, this.entityName, {});
          }

          this.restProviderService.modelValueChanged.subscribe(model => {

            if (this.route.snapshot.queryParams['id']) {

              let bindingProp = this.entityName;

              if (this.parentBindingProperty && this.parentBindingProperty.length > 0) {
                bindingProp = this.parentBindingProperty;
              }

              var res = this.dfms.getValue(model, bindingProp);

              if (typeof res == "undefined") {
                res = this.dfms.getValue(this.model, bindingProp);
              }

              if (typeof res != "undefined" && res != null) {
                if (Array.isArray(res)) {

                  //Is section in o2m. When nested in a row `this.model` IS the row, so use it directly:
                  // the inline-edit template hard-codes `number` to 1, so indexing the list with it would
                  // read a different (already linked) row and mark a fresh row as linked.
                  let sec = nestedInRow ? this.model : res[this.model["number"]];
                  //Prop in section
                  let secProp = this.dfms.getValue(sec, this.entityName);

                  if (secProp) {
                    if (Object.keys(secProp).length > 1) {
                      this.dataIsLinked = true;
                    }
                  }
                } else {
                  if (Object.keys(res).length > 1) {
                    this.dataIsLinked = true;
                  }
                }
              } else {
              }

              //Init value for custom inputs instead of selecting from popup
              // (on the row when nested in a one-to-many, on the emitted root model otherwise)

              const initTarget = nestedInRow ? this.model : model;
              var val = this.dfms.getValue(initTarget, this.entityName);
              if (!val) {
                this.dfms.assign(initTarget, this.entityName, {});
              }


              /*if (typeof res != "undefined" && res != null && Object.keys(res).length > 1) {

                for (var prop in res) {
                  if (Object.prototype.hasOwnProperty.call(res, prop)) {
                    if (res[prop] != null && typeof res[prop] != "undefined") {
                      if (Object.keys(res[prop]).length > 1) {

                        this.dataIsLinked = true;
                      }
                    }
                  } else {
                    this.dataIsLinked = true;
                  }
                }
              } else {
                if (props.length == 0) {
                  this.model[this.entityName] = {};
                }
                else {
                  this.model = this.dfms.assign(this.model, this.entityName, {});
                }
              }*/



            } else {
              this.dataIsLinked = false;
            }

            // Ensure view updates after model value changes
            try { this.changeDetection.markForCheck(); } catch (e) {}
          });

        }
      }
      catch (e) {
        console.log(e);
      }
    }

  }

  ngAfterViewInit() {
    //console.log("FORM", this.form);
    this.updateValidators();
    if (this.inlineSearchActive) {
      // Linked fields render after this control; apply once now and once after the form settles.
      setTimeout(() => this.inlineApplyLinkedVisibility(), 0);
      setTimeout(() => this.inlineApplyLinkedVisibility(), 600);
    }
    //this.form.form.get("model.Person.FirstName").disable();
    //this.form.form.controls["model.Person.FirstName"].disable();
    //this.form.form.controls["model.Person.FirstName"].setValidators(null);

    /*    const _form = this;
        this.form.statusChanges.subscribe(data => {
          try {
            //_form.form.form.get("model.Person.FirstName").disable();
            _form.form.form.controls["model.Person.FirstName"].disable();
            //_form.form.form.controls["model.Person.FirstName"].setValidators(null);
          } catch (e) { }
        })*/

    /*for (var i = 0; i < this.dfms.metaModel.length; i++) {
      if (this.dfms.metaModel[i].Path.includes(this.entityName)) {
        this.dfms.metaModel[i].R = 0;
      }
    }*/

  }

  openDialog() {

    this.dfms.globalForm.form.markAsDirty();
    let formname = null;

    if (this.settingsFormName != null && typeof this.settingsFormName != "undefined" && this.settingsFormName.length > 0) {
      formname = this.settingsFormName;
    } else {
      formname = this.model["SerializationObjectFormLink"];
    }

    let dialogRef = null;

    if (typeof (this.dialogType) == "undefined" || this.dialogType == null || this.dialogType.length == 0) {

      // Multi-select: pre-check the entities already linked in the target one-to-many rows
      let preSelectedItems: any[] = [];
      if (this.enableMultiSelect) {
        const existingArray = this.getMultiSelectTargetArray(false);
        if (Array.isArray(existingArray)) {
          preSelectedItems = existingArray
            .map(section => this.dfms.getValue(section, this.entityName))
            .filter(item => item && item.Id);
        }
      }

      dialogRef = this.dialog.open(SemDialogComponent, {
        // wg-m2o-dialog: redesigned picker look (sem-dialog.component.css + the small overlay block in tailwind.css)
        width: '720px', maxHeight: '85vh', panelClass: 'wg-m2o-dialog', data:
        {
          entityModel: this.modelName,
          title: this.dialogTitle,
          model: this.model,
          formName: formname,
          propName: this.entityName,
          searchFields: this.searchFields,
          displayFields: this.displayFields,
          additionalSearchFilter: this.additionalSearchFilter,
          tooltipTextPoVseh: this.tooltipTextPoVseh,
          repositories: this.repositories,
          onValueSelectValidation: this.onValueSelectValidation,
          enableMultiSelect: this.enableMultiSelect,
          preSelectedItems: preSelectedItems,
          onSelectionChange: (items: any[]) => {
            this.multiSelectItems = items;
            this.selectedItemsChange.emit(items);
          }
        }
      });
    }
    else if (this.dialogType === "tree") {
      dialogRef = this.dialog.open(SemTreeviewDialogComponent, { width: '600px', height: '640px', data: { entityModel: this.modelName, title: this.dialogTitle, formName: formname, propName: this.entityName, searchFields: this.searchFields, displayFields: this.displayFields, additionalSearchFilter: this.additionalSearchFilter, customApiUrl: this.customApiUrl } });
    }

    dialogRef.afterClosed().subscribe(result => {

      if (typeof result != "undefined") {

        // Multi-select: the dialog returns an array of full entity models
        if (this.enableMultiSelect && Array.isArray(result)) {
          if (this.inlineMultiActive) {
            // InlineSearch: the dialog only adds; unticking does not delete rows.
            this.applyMultiSelectAddOnly(result);
            return;
          }
          this.applyMultiSelectResult(result);
          return;
        }

        this.applySingleResult(result);
      }
    });

  }

  /** Writes a picked entity into the model (dialog result and inline pick share this path). */
  private applySingleResult(result: any) {

    this.dataIsLinked = true;
    //this.model[this.entityName] = result;

    if (this.entityName.length == 0) {
      const callerFunc = new Function("callerThis", "result", this.customEvalCode);
      callerFunc(this, result);
      //eval(this.customEvalCode).bind(this);
      result["SerializationObjectFormLink"] = this.model["SerializationObjectFormLink"];
      this.model = null;
      result["Id"] = 0;
      this.model = result;
      this.model = this.dfms.setInitValues(this.model);
      this.dfms._modelValueReset.next(this.model);
    } else {
      this.model = this.dfms.assign(this.model, this.entityName, result, true);
      this.model = this.dfms.setInitValues(this.model);
    }

    if (this.entityReadOnly) {
      this.dfms.manyToOneReadOnly.push(this.entityName);
    }
    this.changeDetection.detectChanges();
    this.updateValidators();
    this.dfms._modelMTOValueChanged.next(this.model);
    this.inlineApplyLinkedVisibility();
  }

  updateValidators() {

  }

  /** Root form model (the one-to-many rows live on it, not on the row this control is nested in). */
  private getRootModel(): any {
    let root = this.restProviderService._modelValueChanged.getValue();
    if (root == null || typeof root == "undefined" || Object.keys(root).length == 0) {
      root = this.dfms.globalModel;
    }
    if (root == null || typeof root == "undefined") {
      root = this.model;
    }
    return root;
  }

  private getMultiSelectTargetBinding(): string {
    if (this.multiSelectTargetBinding && this.multiSelectTargetBinding.length > 0) {
      return this.multiSelectTargetBinding;
    }
    return this.parentBindingProperty || "";
  }

  /** The one-to-many array multi-select writes into. With `create`, an empty array is assigned on the root model. */
  private getMultiSelectTargetArray(create: boolean): any[] {
    const binding = this.getMultiSelectTargetBinding();
    if (binding.length == 0) {
      return null;
    }

    const root = this.getRootModel();
    let target = this.dfms.getValue(root, binding);

    if (typeof target == "undefined" || target == null) {
      if (!create) {
        return null;
      }
      target = [];
      this.dfms.assign(root, binding, target, true);
    } else if (!Array.isArray(target)) {
      target = [target];
      this.dfms.assign(root, binding, target, true);
    }

    return target;
  }

  /**
   * Syncs the target one-to-many rows with the entities picked in the dialog:
   * - drops the row this control was opened from if nothing is linked on it yet,
   * - removes rows whose entity was unchecked,
   * - appends one row per newly checked entity ({ [entityName]: entity }).
   */
  private applyMultiSelectResult(result: any[]) {

    this.multiSelectItems = result;
    this.selectedItemsChange.emit(result);

    const targetArray = this.getMultiSelectTargetArray(true);
    if (targetArray == null) {
      return;
    }

    const currentEntity = this.dfms.getValue(this.model, this.entityName);
    const currentRowEmpty = !currentEntity || typeof currentEntity !== "object" || !currentEntity.Id;
    if (currentRowEmpty) {
      const idx = targetArray.indexOf(this.model);
      if (idx > -1) {
        targetArray.splice(idx, 1);
      }
    }

    const resultIds = result.map(item => item.Id);

    for (let i = targetArray.length - 1; i >= 0; i--) {
      const entity = this.dfms.getValue(targetArray[i], this.entityName);
      if (entity && entity.Id && !resultIds.includes(entity.Id)) {
        targetArray.splice(i, 1);
      }
    }

    const existingIds = targetArray
      .map(section => this.dfms.getValue(section, this.entityName)?.Id)
      .filter(id => id);

    for (const item of result) {
      if (!existingIds.includes(item.Id)) {
        const newSection = this.dfms.assign({}, this.entityName, item, true);
        targetArray.push(newSection);
      }
    }

    this.dfms.globalForm.form.markAsDirty();
    this.dfms._modelMTOValueChanged.next(this.getRootModel());
    this.changeDetection.detectChanges();
  }

  /** Inline pick in add-to-list mode: the row holding this field stays (last), so the field keeps focus for the next pick. */
  private applyInlinePickKeepingField(resolved: any) {
    const targetArray = this.getMultiSelectTargetArray(true);
    if (targetArray == null) {
      return;
    }
    const container: HTMLElement = this.control && this.control.nativeElement ? this.control.nativeElement.closest("sem-one-to-many-container") : null;
    applyAddKeepingField(targetArray, this.model, [resolved], this.entityName, (o, p) => this.dfms.getValue(o, p), (o, p, v, c) => this.dfms.assign(o, p, v, c));
    // the kept add-field row is empty: it must not reach the server (sem-dynamic submit prunes the POST copy)
    registerInlineAddRows(this.getRootModel()["SerializationObjectFormLink"], this.getMultiSelectTargetBinding(), this.entityName);
    this.multiSelectItems = targetArray.map(row => this.dfms.getValue(row, this.entityName)).filter(e => e && e.Id);
    this.selectedItemsChange.emit(this.multiSelectItems);
    this.dfms.globalForm.form.markAsDirty();
    this.dfms._modelMTOValueChanged.next(this.getRootModel());
    this.changeDetection.detectChanges();
    this.inlineReopenAddField(container);
  }

  /**
   * An inline-edit grid (OKS Storitve) closes its row editor when the rows change, which destroys this field.
   * Re-open the editor on the add-field row (kept last) through the grid's own edit action, then focus the new
   * field: its focus handler opens the list again with empty text.
   */
  private inlineReopenAddField(container: HTMLElement) {
    if (!container) {
      return;
    }
    const oldHost: HTMLElement = this.control ? this.control.nativeElement : null;
    const visibleField = () => {
      const input = container.querySelector("input.m2o-inline-input") as HTMLInputElement;
      return input && input.offsetParent !== null ? input : null;
    };
    const focusNewField = (tries: number) => {
      const input = visibleField();
      if (input) {
        input.focus();
      } else if (tries > 0) {
        setTimeout(() => focusNewField(tries - 1), 50);
      }
    };
    // An inline-edit grid closes its editor ~100–300 ms after the rows change: wait (max 2 s) until this field is gone.
    const waitForClose = (tries: number) => {
      const stillHere = oldHost && oldHost.isConnected && oldHost.offsetParent !== null;
      if (stillHere) {
        if (tries > 0) {
          setTimeout(() => waitForClose(tries - 1), 50);
        }
        return;
      }
      if (visibleField()) {
        focusNewField(0);
        return;
      }
      const rows = Array.from(container.querySelectorAll(":scope > .grid-view-listing > table > tbody > tr")).filter(tr => tr.querySelector(".o2m-row-actions"));
      const last = rows[rows.length - 1];
      // the grid binds (click)="inEditSection(..)" on the span.ti-edit itself, not on its <a>
      const edit = last ? (last.querySelector(".o2m-row-actions .ti-edit") as HTMLElement) : null;
      if (edit) {
        edit.click();
        focusNewField(20);
      }
    };
    setTimeout(() => {
      // a plain o2m keeps this field: focus it now; an inline-edit grid destroys it shortly after (handled below)
      focusNewField(0);
      waitForClose(40);
    }, 0);
  }

  /** InlineSearch + EnableMultiSelect: entities are appended to the target rows, never removed. */
  private applyMultiSelectAddOnly(result: any[]) {
    const targetArray = this.getMultiSelectTargetArray(true);
    if (targetArray == null) {
      return;
    }
    applyAddOnly(targetArray, this.model, result, this.entityName, (o, p) => this.dfms.getValue(o, p), (o, p, v, c) => this.dfms.assign(o, p, v, c));
    this.multiSelectItems = targetArray.map(row => this.dfms.getValue(row, this.entityName)).filter(e => e && e.Id);
    this.selectedItemsChange.emit(this.multiSelectItems);
    this.dfms.globalForm.form.markAsDirty();
    this.dfms._modelMTOValueChanged.next(this.getRootModel());
    this.changeDetection.detectChanges();
  }

  showLinkDataControls(entityName: string) {

    if (this.readOnly == true) {
      return false;
    }

    // bosko had this on .html we moved this here
    if (this.checkForDisabled(entityName) || this.dfms?.isFormReadOnly === true) {
      return false;
    }

    if (typeof this.readOnlyCondition != "undefined" && this.readOnlyCondition) {
      if (this.readOnlyCondition.length > 0) {
        let resulteval = eval(this.readOnlyCondition);

        // if condition for read only is true we dont show linkdatacontrols	
        if (resulteval == true) {
          return false;
        }
      }
    }
    return true;
  }

  removeLinkedData() {
    // this.model[this.entityName] = {};
    this.dfms.globalForm.form.markAsDirty();
    this.model = this.dfms.assign(this.model, this.entityName, {}, true);
    this.model = this.dfms.setInitValues(this.model);
    this.dataIsLinked = false;
    this.dfms._modelMTOValueChanged.next(null);
    this.inlineApplyLinkedVisibility();

    if (this.entityReadOnly) {
      for (var i = 0; i < this.dfms.manyToOneReadOnly.length; i++) {
        if (this.dfms.manyToOneReadOnly[i] == this.entityName) {
          this.dfms.manyToOneReadOnly.splice(i, 1);
          break;
        }
      }
    }

    if (typeof this.clearFiles != "undefined" && this.clearFiles) {
      if (this.clearFiles.length > 0 && this.clearFiles.toLowerCase() == "true") {
        this.fileUploadService._clearFiles.next(null);
      }
    }

  }

  // ---- InlineSearch ----

  /** Text of the linked entity: first DisplayTableFields value, else Title. */
  get inlineSelectedText(): string {
    const entity = this.dfms.getValue(this.model, this.entityName);
    if (entity == null || typeof entity != "object" || !entity.Id) {
      return "";
    }
    const fields = defaultDisplayFields(this.displayFields, k => this.translationsService.translate(k));
    const hit = toHit(entity, fields, (o, p) => this.dfms.getValue(o, p));
    return inlineDisplayText(hit.title, this.inlineEmptyNameLabel);
  }

  get inlineEmptyNameLabel(): string {
    const t = this.translationsService.translations ? this.translationsService.translations["WebGalis_NoName"] : null;
    return t || "(brez naziva)";
  }

  get inlineHasValue(): boolean {
    const entity = this.dfms.getValue(this.model, this.entityName);
    return entity != null && typeof entity == "object" && !!entity.Id;
  }

  get inlineCanEdit(): boolean {
    return !this.readOnly && this.showLinkDataControls(this.entityName);
  }

  get advancedSearchLabel(): string {
    const t = this.translationsService.translations ? this.translationsService.translations["WebGalis_AdvancedSearch"] : null;
    return t || "Napredno iskanje";
  }

  private inlineFormName(): string {
    if (this.settingsFormName != null && typeof this.settingsFormName != "undefined" && this.settingsFormName.length > 0) {
      return this.settingsFormName;
    }
    return this.model["SerializationObjectFormLink"];
  }

  inlineStartEditing() {
    if (!this.inlineCanEdit) {
      return;
    }
    this.inlineEditing = true;
    this.inlineText = "";
    this.inlineError = null;
    this.changeDetection.detectChanges();
    const input = this.control.nativeElement.querySelector("input.m2o-inline-input");
    if (input) {
      input.focus();
    }
    this.inlineQueue();
  }

  inlineOnInput(value: string) {
    this.inlineText = value;
    // Hide stale hits at once: the list shows only rows matching the new text until the search returns.
    this.inlineHits = narrowHits(this.inlineHits, value, this.inlineSearchProps(), (o, p) => this.dfms.getValue(o, p));
    this.inlineActive = 0;
    this.inlineLoading = true;
    this.inlineQueue();
  }

  /** Properties the typed text is matched against: text SearchFields, else the display fields. */
  private inlineSearchProps(): string[] {
    const searchProps = ((this.searchFields || []) as any[]).filter(f => f && f.Type != 'Date' && f.Type != 'CB').map(f => f.PropertyName);
    return searchProps.length > 0 ? searchProps : defaultDisplayFields(this.displayFields, k => this.translationsService.translate(k)).map(f => f.PropertyName);
  }

  private inlineQueue() {
    if (this.inlineTimer) {
      clearTimeout(this.inlineTimer);
    }
    this.inlineTimer = setTimeout(() => this.inlineRun(), 250);
  }

  private inlineRun(fallback: boolean = false) {
    const seq = ++this.inlineSeq;
    if (this.inlineRequest) {
      this.inlineRequest.unsubscribe();
    }
    const typed = this.inlineText || "";
    const fields = defaultDisplayFields(this.displayFields, k => this.translationsService.translate(k));
    const query = buildInlineSearchQuery({
      entityModel: this.modelName,
      searchFields: this.searchFields,
      displayFields: fields,
      additionalSearchFilter: this.additionalSearchFilter,
      currentFormModel: this.model,
      repositories: this.repositories,
      typed: fallback ? "" : typed,
      pageSize: fallback ? SemManyToOneControlComponent.INLINE_FALLBACK_PAGE_SIZE : SemManyToOneControlComponent.INLINE_PAGE_SIZE
    });
    this.inlineLoading = true;
    this.inlineOpen = true;
    this.inlineRequest = this.searchService.performSearch(query, null, null, null).subscribe(data => {
      if (seq != this.inlineSeq) {
        return;
      }
      const models = (data && data["Models"] ? data["Models"] : []).map(m => m["Model"]);
      const props = this.inlineSearchProps();
      // Word-start + diacritic-insensitive matching is done here on the returned page (A1: backend search unchanged).
      const matching = typed.trim().length == 0 ? models : models.filter(m =>
        props.some(p => matchesWordStart("" + (this.dfms.getValue(m, p) ?? ""), typed)));
      if (matching.length == 0 && !fallback && typed.trim().length > 0) {
        // The index may not fold diacritics ("vucedol*" vs "Vučedol"): retry once without the text filter.
        this.inlineRun(true);
        return;
      }
      this.inlineHits = matching.slice(0, SemManyToOneControlComponent.INLINE_MAX_HITS).map(m => toHit(m, fields, (o, p) => this.dfms.getValue(o, p)));
      this.inlineActive = 0;
      this.inlineLoading = false;
      this.changeDetection.detectChanges();
    }, () => {
      if (seq != this.inlineSeq) {
        return;
      }
      this.inlineHits = [];
      this.inlineLoading = false;
      this.changeDetection.detectChanges();
    });
  }

  /** Index inlineHits.length is the "Napredno iskanje" row. */
  inlineOnKeydown(event: KeyboardEvent) {
    const last = this.inlineHits.length;
    if (event.key == "ArrowDown") {
      event.preventDefault();
      this.inlineOpen = true;
      this.inlineActive = this.inlineActive >= last ? 0 : this.inlineActive + 1;
    } else if (event.key == "ArrowUp") {
      event.preventDefault();
      this.inlineActive = this.inlineActive <= 0 ? last : this.inlineActive - 1;
    } else if (event.key == "Enter") {
      event.preventDefault();
      if (this.inlineActive < last) {
        this.inlinePick(this.inlineHits[this.inlineActive]);
      } else {
        this.inlineAdvanced();
      }
    } else if (event.key == "Escape") {
      event.preventDefault();
      this.inlineClose();
    }
  }

  inlineClose() {
    if (this.inlineTimer) {
      clearTimeout(this.inlineTimer);
    }
    this.inlineSeq++;
    this.inlineOpen = false;
    this.inlineEditing = false;
    this.inlineLoading = false;
    this.inlineText = "";
    this.changeDetection.detectChanges();
  }

  /** Blur closes the list unless focus moved into it (mousedown on a row keeps it open). */
  inlineOnBlur() {
    setTimeout(() => {
      if (!this.control.nativeElement.contains(document.activeElement)) {
        this.inlineClose();
      }
    }, 150);
  }

  /** Same resolve + OnValueSelectValidation + assign path as a dialog pick. */
  inlinePick(hit: InlineHit) {
    if (!hit) {
      return;
    }
    this.dfms.globalForm.form.markAsDirty();
    this.inlineError = null;
    this.inlineLoading = true;
    const url = buildRelatedEntityUrl(this.inlineFormName(), this.entityName, this.modelName, hit.model);
    this.restProviderService.getDataGET(url).subscribe(data => {
      const resolved = JSON.parse(data)["Model"];
      const validation = this.onValueSelectValidation || "";
      if (validation.length > 0 && validation.includes("formAction")) {
        const splited = validation.split(":");
        this.actionsService.executeAction(this.actionsService.getAction(splited[1]), {}, resolved, (ok: any, error: any, context: any) => {
          if (ok) {
            context.inlineFinishPick(resolved);
          } else {
            context.inlineLoading = false;
            context.inlineError = context.translationsService.translate(splited[2]);
            context.changeDetection.detectChanges();
          }
        }, null, this, null);
      } else if (validation.length == 0) {
        this.inlineFinishPick(resolved);
      } else {
        // Same as SemDialogComponent: a non-formAction validation string never completes the pick.
        this.inlineLoading = false;
        this.changeDetection.detectChanges();
      }
    }, () => {
      this.inlineLoading = false;
      this.changeDetection.detectChanges();
    });
  }

  inlineFinishPick(resolved: any) {
    this.inlineLoading = false;
    if (this.inlineMultiActive) {
      this.inlineAddToList(resolved);
      return;
    }
    this.inlineClose();
    this.applySingleResult(resolved);
  }

  private inlineLastCanEdit: boolean = null;

  /** Read-only <-> edit (UREDI) keeps this control: re-apply the row look and linked visibility for the new mode. */
  ngAfterViewChecked() {
    if (!this.inlineSearchActive) {
      return;
    }
    const canEdit = this.inlineCanEdit;
    if (canEdit !== this.inlineLastCanEdit) {
      this.inlineLastCanEdit = canEdit;
      setTimeout(() => this.inlineApplyLinkedVisibility(), 0);
      setTimeout(() => this.inlineApplyLinkedVisibility(), 600);
    }
  }

  /** True while the linked fields are folded into one muted line (InlineSearch, nothing picked, 2+ linked fields). */
  inlineCollapsed: boolean = false;

  /**
   * Linked fields = controls whose binding (name attribute) starts with `<entityName>.`, as children of the
   * container or as sibling rows in the same tab (BRD RingingPlace.*). Hides the one repeating the title and,
   * while nothing is picked and there are 2+, all of them. Only runs when InlineSearch is active.
   */
  inlineApplyLinkedVisibility() {
    if (!this.inlineSearchActive || !this.control || !this.control.nativeElement) {
      return;
    }
    const host: HTMLElement = this.control.nativeElement;
    const scope: Element = host.closest(".one-to-many-section-wrapper, sem-form-tab-content, .tab-pane, form") || document.body;
    const prefix = this.entityName + ".";
    const elements = Array.from(scope.querySelectorAll("[name]")) as HTMLElement[];
    const nameOf = (el: HTMLElement) => bindingOfElementName(el.getAttribute("name"));
    const candidates = elements.filter(el => !host.contains(el) && nameOf(el).startsWith(prefix));
    const fields = defaultDisplayFields(this.displayFields, k => this.translationsService.translate(k));
    const plainRow = inlineRowMode(!!host.closest(".o2mSection"), !!host.closest(".grid-view-listing, .inline-edit")) == "row";
    const result = bindingsToHide(candidates.map(nameOf), this.entityName, fields, this.inlineHasValue, this.inlineMultiActive, plainRow);
    for (const el of candidates) {
      const wrapper = this.inlineLinkedWrapper(el, host);
      if (result.hide.includes(nameOf(el))) {
        wrapper.style.setProperty("display", "none", "important");
        wrapper.setAttribute("data-m2o-inline-hidden", "1");
      } else if (wrapper.getAttribute("data-m2o-inline-hidden") == "1") {
        wrapper.style.removeProperty("display");
        wrapper.removeAttribute("data-m2o-inline-hidden");
      }
    }
    this.inlineCollapseEmptyRows(scope, host);
    this.inlineApplyRowLook(host);
    if (this.inlineCollapsed != result.collapsed) {
      this.inlineCollapsed = result.collapsed;
      this.changeDetection.detectChanges();
    }
  }

  /** Plain o2m row whose only visible input is this picker (never an inline-edit grid row editor). */
  private inlineClearRemovesRow(): boolean {
    if (!this.inlineSearchActive || !this.control || !this.control.nativeElement) {
      return false;
    }
    const host: HTMLElement = this.control.nativeElement;
    const inRow = !!host.closest(".o2mSection");
    const inGrid = !!host.closest(".grid-view-listing, .inline-edit");
    return inlineClearRemovesRow(inRow, inGrid, inRow && !inGrid && this.inlineOwnsRow(), !!this.inlineMultiActive);
  }

  inlineOwnsRow(): boolean {
    if (!this.inlineSearchActive || !this.control || !this.control.nativeElement) {
      return false;
    }
    const host: HTMLElement = this.control.nativeElement;
    const section = host.closest(".o2mSection") as HTMLElement;
    const inGrid = !!host.closest(".grid-view-listing, .inline-edit");
    if (inlineRowMode(!!section, inGrid) != "row") {
      return false;
    }
    const others = Array.from(section.querySelectorAll("input, select, textarea")) as HTMLElement[];
    return others.every(el => host.contains(el) || el.offsetParent == null || (el as HTMLInputElement).type == "hidden");
  }

  /**
   * A picker that owns its o2m row: the row card loses its own frame (the value box / read-only card is the row),
   * and the row menu (⋮) goes when it only offers Delete, which × now does. Inline styles, reverted when the row changes.
   */
  private inlineApplyRowLook(host: HTMLElement) {
    const section = host.closest(".o2mSection") as HTMLElement;
    if (!section) {
      return;
    }
    const owns = this.inlineOwnsRow();
    const emptyClear = inlineEmptyRowClear(owns, this.inlineHasValue, this.inlineMultiActive);
    if (emptyClear != this.inlineEmptyClear) {
      this.inlineEmptyClear = emptyClear;
      this.changeDetection.detectChanges();
    }
    this.inlineHookAddNew(host, owns);
    const flat: [string, string][] = [["padding", "0"], ["border", "0"], ["background", "transparent"], ["box-shadow", "none"]];
    if (owns) {
      flat.forEach(([k, v]) => section.style.setProperty(k, v, "important"));
      // the platform gives the picker host a 25 px right padding in read-only: the card would end short of the row
      host.style.setProperty("padding-right", "0", "important");
      section.setAttribute("data-m2o-inline-row", "1");
    } else if (section.getAttribute("data-m2o-inline-row") == "1") {
      flat.forEach(([k]) => section.style.removeProperty(k));
      host.style.removeProperty("padding-right");
      section.removeAttribute("data-m2o-inline-row");
    }
    const menu = section.querySelector(":scope > .o2m-section-additional-actions") as HTMLElement;
    if (!menu) {
      return;
    }
    const o2m = this._injector.get(OneToManySectionComponent, null);
    // same filter as the row menu template: an action shows when it has no Condition or its Condition holds
    const shownActions = o2m && o2m.actions ? (o2m.actions as any[]).filter(a => a.Condition == null || (o2m as any).evaluateCondition(a.Condition)) : [];
    const onlyDelete = !!o2m && shownActions.length == 0 && !o2m.orderEnabled;
    if (inlineHideRowMenu(owns, this.inlineHasValue, this.inlineMultiActive, onlyDelete)) {
      menu.style.setProperty("display", "none", "important");
      menu.setAttribute("data-m2o-inline-hidden", "1");
    } else if (menu.getAttribute("data-m2o-inline-hidden") == "1") {
      menu.style.removeProperty("display");
      menu.removeAttribute("data-m2o-inline-hidden");
    }
  }

  /** × on an empty single row the picker owns (its ⋮ is hidden, see inlineHideRowMenu). */
  inlineEmptyClear: boolean = false;

  private inlineAddNewHook: { el: HTMLElement, fn: (ev: Event) => void } = null;

  /**
   * "Nov zapis" of the o2m this picker's row sits in: while the row is still empty, focus it instead of adding a second
   * empty row (Galis Avtorji delavnice, Vodje delavnic). Capture listener on the container, removed with the control.
   */
  private inlineHookAddNew(host: HTMLElement, owns: boolean) {
    const el = owns ? host.closest("sem-one-to-many-container") as HTMLElement : null;
    if (this.inlineAddNewHook && this.inlineAddNewHook.el !== el) {
      this.inlineUnhookAddNew();
    }
    if (!el || this.inlineAddNewHook) {
      return;
    }
    const fn = (ev: Event) => {
      const t = ev.target as HTMLElement;
      const btn = t && t.closest ? t.closest(".manipulate-section.dataIsLinked") as HTMLElement : null;
      if (!btn || btn.closest("sem-one-to-many-container") !== el || btn.closest(".one-to-many-section-wrapper")) {
        return;
      }
      if (!this.control || !inlineRedirectAddNew(this.inlineOwnsRow(), this.inlineHasValue, this.inlineCanEdit)) {
        return;
      }
      const input = this.control.nativeElement.querySelector("input.m2o-inline-input") as HTMLInputElement;
      if (!input) {
        return;
      }
      ev.stopImmediatePropagation();
      ev.preventDefault();
      input.scrollIntoView({ block: "nearest" });
      input.focus();
    };
    el.addEventListener("click", fn, true);
    this.inlineAddNewHook = { el, fn };
  }

  private inlineUnhookAddNew() {
    if (this.inlineAddNewHook) {
      this.inlineAddNewHook.el.removeEventListener("click", this.inlineAddNewHook.fn, true);
      this.inlineAddNewHook = null;
    }
  }

  ngOnDestroy() {
    this.inlineUnhookAddNew();
  }

  /**
   * Layout rows (multi-column / column wrappers) left with no visible field after the linked fields were hidden
   * keep their height (BRD Lokaliteta: 67 px each): hide them too. Never an element holding this picker.
   */
  private inlineCollapseEmptyRows(scope: Element, host: HTMLElement) {
    Array.from(scope.querySelectorAll("[data-m2o-inline-hidden='2']")).forEach((el: HTMLElement) => {
      el.style.removeProperty("display");
      el.removeAttribute("data-m2o-inline-hidden");
    });
    Array.from(scope.querySelectorAll("[data-m2o-inline-widened]")).forEach((el: HTMLElement) => {
      ["width", "padding-left", "padding-right", "float"].forEach(k => el.style.removeProperty(k));
      el.removeAttribute("data-m2o-inline-widened");
    });
    const hidden = Array.from(scope.querySelectorAll("[data-m2o-inline-hidden='1']")) as HTMLElement[];
    for (const w of hidden) {
      let a = w.parentElement;
      while (a && a !== scope && scope.contains(a) && !a.contains(host)) {
        // hide only when nothing in it still takes space (a map or a heading keeps it)
        const takesSpace = Array.from(a.children).some((ch: HTMLElement) => ch.offsetParent != null && ch.offsetHeight > 0);
        if (takesSpace || a.getAttribute("data-m2o-inline-hidden")) {
          break;
        }
        a.style.setProperty("display", "none", "important");
        a.setAttribute("data-m2o-inline-hidden", "2");
        a = a.parentElement;
      }
    }
    // a column left alone beside a column we hid takes the whole row, like the plain fields above it
    const rows = new Set<HTMLElement>();
    Array.from(scope.querySelectorAll("[data-m2o-inline-hidden]")).forEach((el: HTMLElement) => el.parentElement && rows.add(el.parentElement));
    rows.forEach(row => {
      if (row.contains(host)) {
        return;
      }
      const kids = Array.from(row.children) as HTMLElement[];
      const i = inlineWidenColumn(kids.map(k => ({ cls: String(k.className), shown: getComputedStyle(k).display != "none", ours: !!k.getAttribute("data-m2o-inline-hidden") })));
      if (i > -1) {
        const col = kids[i];
        [["width", "100%"], ["padding-left", "0"], ["padding-right", "0"], ["float", "none"]].forEach(([k, v]) => col.style.setProperty(k, v, "important"));
        col.setAttribute("data-m2o-inline-widened", "1");
      }
    });
  }

  /** The control's form row (GalisFormControl / form-group), never an element that also holds this picker. */
  private inlineLinkedWrapper(el: HTMLElement, host: HTMLElement): HTMLElement {
    const row = el.closest(".GalisFormControl, .form-group") as HTMLElement;
    return row && !row.contains(host) ? row : el;
  }

  /** "Add to list" mode: InlineSearch + EnableMultiSelect. */
  get inlineMultiActive(): boolean {
    return this.inlineSearchActive && this.enableMultiSelect === true;
  }

  private inlineLinkedIds(): any[] {
    return linkedEntityIds(this.getMultiSelectTargetArray(false), this.entityName, (o, p) => this.dfms.getValue(o, p));
  }

  inlineIsAdded(hit: InlineHit): boolean {
    return this.inlineMultiActive && !!hit && !!hit.model && this.inlineLinkedIds().includes(hit.model.Id);
  }

  /** Picking an already linked entity focuses its row; otherwise one row is appended and the search stays open. */
  private inlineAddToList(resolved: any) {
    const targetArray = this.getMultiSelectTargetArray(true);
    if (targetArray == null) {
      return;
    }
    const getValue = (o, p) => this.dfms.getValue(o, p);
    const existing = findLinkedRowIndex(targetArray, this.entityName, resolved && resolved.Id, getValue);
    if (existing > -1) {
      this.inlineFocusRow(existing);
    } else {
      this.applyInlinePickKeepingField(resolved);
    }
    this.inlineText = "";
    const input = this.control.nativeElement.querySelector("input.m2o-inline-input");
    if (input) {
      input.value = "";
      input.focus();
    }
    this.inlineEditing = true;
    this.inlineQueue();
  }

  /** Scrolls to and highlights the n-th row of the target one-to-many container (best effort, DOM only). */
  private inlineFocusRow(index: number) {
    // The picker sits in a row of the target container (MultiSelectTargetBinding defaults to that container).
    const container = this.control.nativeElement.closest("sem-one-to-many-container");
    const rows = container ? container.querySelectorAll(".one-to-many-section-wrapper") : null;
    const row = rows && rows.length > index ? rows[index] as HTMLElement : null;
    if (row) {
      row.scrollIntoView({ block: "nearest" });
      row.classList.add("m2o-inline-row-flash");
      setTimeout(() => row.classList.remove("m2o-inline-row-flash"), 1200);
    }
  }

  inlineClear() {
    if (!this.inlineCanEdit) {
      return;
    }
    if (this.inlineClearRemovesRow()) {
      // the picker is the row: × removes the row at once, like × clears a form-level picker. Nothing is stored before
      // Shrani, so no dialog (the container's own remove asks with the leave-page text). Same follow-up as removeSection.
      const o2m = this._injector.get(OneToManySectionComponent, null);
      const container = this._injector.get(SemOneToManyContainerComponent, null) as any;
      if (o2m && o2m.section && container) {
        const values = this.dfms.getValue(container.model, container.bindingPropertyName);
        if (inlineRemoveRow(values, o2m.section) > -1) {
          try {
            this.dfms.globalForm.form.markAsDirty();
          } catch (e) {
          }
          container.currentNumber = values.length;
          if (container.hideSectionWhenNotEditing) {
            container.gridSection = null;
          }
          container.fireSectionAddedEvent();
          container.executeAfterRemoveSectionLogic();
          return;
        }
      }
      if (o2m && o2m.section) {
        o2m.removeSection(o2m.section);
        return;
      }
    }
    this.removeLinkedData();
    this.changeDetection.detectChanges();
  }

  inlineAdvanced() {
    this.inlineClose();
    this.openDialog();
  }

  navigateToEntity() {

    if (typeof this.entityFormUrl != "undefined" && this.entityFormUrl != null && this.entityFormUrl.length > 0) {
      let entity = this.dfms.getValue(this.model, this.entityName);

      if (typeof entity != "undefined" && entity != null) {
        if (typeof entity["Id"] != "undefined" && entity["Id"] != 0) {
          this.utility.processNavigationUrl(this.entityFormUrl + "?id=" + entity["Id"]);
        }
      }
    }
  }



}
